import { MicrophoneIcon, useCameraStore, VideoIcon } from '@flik/shared';
import {
  CameraManager,
  UserCircularAvatar,
  useTheme,
} from '@flik/shared/components';
import React, { useEffect, useRef } from 'react';
import MicrophoneManager from '../managers/MicrophoneManager';
import { useMicrophoneStore } from '../stores/microphoneStore';
import MicrophoneRing from './MicrophoneRing';
import iconPng from '/icons/icon.png';

export interface SessionInvitation {
  id: string;
  sessionId: string;
  sessionTitle: string;
  createdBy: string;
  inviteLink: string;
  timestamp: number;
}

interface SessionInvitationModalProps {
  invitation: SessionInvitation | null;
  isOpen: boolean;
  onAccept: (invitation: SessionInvitation) => void;
  onDecline: () => void;
}

const SessionInvitationModal: React.FC<SessionInvitationModalProps> = ({
  invitation,
  isOpen,
  onAccept,
  onDecline,
}) => {
  const { provideTailwind } = useTheme();
  const { cameraStream, isVideoOn, setIsVideoOn, resetCameraState } =
    useCameraStore();
  const { isMicOn, setIsMicOn, resetMicrophoneState } = useMicrophoneStore();
  const videoRef = useRef<HTMLVideoElement>(null);

  // Force camera OFF every time the modal opens
  useEffect(() => {
    if (isOpen) {
      resetCameraState();
    }
  }, [isOpen, resetCameraState]);

  // Update video element when camera stream changes
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.srcObject = cameraStream;
    }
  }, [cameraStream]);

  const toggleVideo = () => {
    setIsVideoOn(!isVideoOn);
  };

  const toggleMicrophone = () => {
    setIsMicOn(!isMicOn);
  };

  if (!isOpen || !invitation) {
    return null;
  }

  const handleAccept = () => {
    onAccept(invitation);
  };

  const handleDecline = () => {
    onDecline();
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleDecline();
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      onClick={handleBackdropClick}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/10 backdrop-blur-sm" />

      {/* Media Managers */}
      <CameraManager isVideoOn={isVideoOn} />
      <MicrophoneManager isMicOn={isMicOn} />

      {/* Modal */}
      <div
        className={`relative w-full max-w-lg ${provideTailwind(
          'bg-secondary',
        )} rounded-2xl shadow-2xl border-2 ${provideTailwind(
          'border-primary',
        )} border-opacity-20 overflow-hidden`}
      >
        {/* Header */}
        <div className={`p-6 ${provideTailwind('bg-primary')} bg-opacity-10`}>
          <div className="flex items-center space-x-3">
            <UserCircularAvatar
              identity={invitation.createdBy}
              size="md"
              className="bg-pink-500"
              tooltipPosition="right"
            />
            <div>
              <h2
                className={`text-xl font-semibold ${provideTailwind(
                  'text-primary',
                )}`}
              >
                {invitation.sessionTitle}
              </h2>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="space-y-6">
            {/* Camera Testing Section */}
            <div className="text-center">
              <p
                className={`text-sm mb-4 ${provideTailwind('text-secondary')}`}
              >
                Test your camera before joining
              </p>

              {/* Camera Preview Circle */}
              <div className="relative mb-6 flex justify-center">
                <div className="w-48 h-48 rounded-full bg-black flex items-center justify-center relative overflow-hidden shadow-lg">
                  {/* Microphone Ring */}
                  <MicrophoneRing size={192} strokeWidth={6} />
                  {/* Video feed when video is on */}
                  {isVideoOn && cameraStream && (
                    <video
                      ref={videoRef}
                      autoPlay
                      muted
                      playsInline
                      className="w-full h-full object-cover rounded-full"
                    />
                  )}

                  {/* Flik logo when video is off */}
                  {!isVideoOn && (
                    <div className="relative z-10 flex items-center justify-center">
                      <img
                        src={iconPng}
                        alt="Flik"
                        width="120"
                        height="120"
                        className="text-white"
                      />
                    </div>
                  )}
                </div>

                {/* Video toggle button */}
                <div
                  className={`absolute w-10 h-10 rounded-full flex items-center justify-center transition-all duration-200 cursor-pointer hover:scale-110 ${
                    isVideoOn ? 'bg-green-500' : 'bg-red-500'
                  }`}
                  style={{
                    zIndex: 999,
                    bottom: 'calc(50% - 60px)',
                    right: 'calc(50% - 60px)',
                  }}
                  onClick={toggleVideo}
                >
                  <VideoIcon width={16} height={16} className="text-white" />
                </div>

                {/* Microphone toggle button */}
                <div
                  className={`absolute w-10 h-10 rounded-full flex items-center justify-center transition-all duration-200 cursor-pointer hover:scale-110 ${
                    isMicOn ? 'bg-green-500' : 'bg-red-500'
                  }`}
                  style={{
                    zIndex: 999,
                    bottom: 'calc(50% - 60px)',
                    left: 'calc(50% - 60px)',
                  }}
                  onClick={toggleMicrophone}
                >
                  <MicrophoneIcon
                    width={16}
                    height={16}
                    className="text-white"
                  />
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-3">
              <button
                onClick={handleDecline}
                className={`flex-1 py-3 px-6 ${provideTailwind(
                  'bg-secondary',
                )} ${provideTailwind('hover:bg-tertiary')} ${provideTailwind(
                  'text-primary',
                )} rounded-xl transition-all duration-200 font-medium border ${provideTailwind(
                  'border-primary',
                )} border-opacity-10 hover:border-opacity-20`}
              >
                Decline
              </button>
              <button
                onClick={handleAccept}
                className={`flex-1 py-3 px-6 bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white rounded-xl transition-all duration-200 font-medium shadow-lg hover:shadow-xl transform hover:scale-[1.02]`}
              >
                Join Flik
              </button>
            </div>
          </div>
        </div>

        {/* Subtle border glow */}
        <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-pink-500/20 via-purple-500/20 to-blue-500/20 opacity-0 hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
      </div>
    </div>
  );
};

export default SessionInvitationModal;
